
import { GameRole } from '../../types/game';
import { skills } from '../skills';
import { challenges } from '../challenges';

export const engineerRole: GameRole = {
  id: 'engineer',
  title: 'Firmware Engineer',
  description: 'Specialize in optimizing mining software and firmware. Improve hashing efficiency, troubleshoot software issues, and develop custom solutions.',
  difficulty: 'Hard',
  strengths: [
    'Best at efficiency optimization',
    'Strong technical skill advancement',
    'Unmatched hardware performance'
  ],
  startingSkills: [
    skills.find(s => s.id === 'firmware-tuning')!
  ],
  careerPath: [
    {
      level: 'Junior',
      title: 'Firmware Engineer',
      requiredExperience: 0,
      requiredYears: 0
    },
    {
      level: 'Mid-Level',
      title: 'Senior Firmware Engineer',
      requiredExperience: 150,
      requiredYears: 3,
      pathOptions: [
        {
          path: 'Technical',
          title: 'Firmware Optimization Specialist',
          description: 'Focus on creating highly efficient firmware for maximum performance gains.',
          requiredSkills: ['firmware-tuning', 'advanced-diagnostics'],
          kpiBoosts: {
            efficiency: 5,
            uptime: 2
          }
        },
        {
          path: 'Research',
          title: 'Mining R&D Engineer',
          description: 'Focus on researching cutting-edge mining techniques and technologies.',
          requiredSkills: ['firmware-tuning', 'network-optimization'],
          kpiBoosts: {
            efficiency: 3,
            profitMargin: 2
          }
        }
      ]
    },
    {
      level: 'Senior',
      title: 'Engineering Director',
      requiredExperience: 350,
      requiredYears: 6,
      pathOptions: [
        {
          path: 'Technical',
          title: 'Chief Firmware Architect',
          description: 'Lead the development of proprietary firmware solutions that dramatically improve mining efficiency.',
          requiredSkills: ['custom-firmware-development', 'advanced-diagnostics'],
          kpiBoosts: {
            efficiency: 8,
            uptime: 3
          }
        },
        {
          path: 'Management',
          title: 'Engineering Team Lead',
          description: 'Lead a team of engineers, balancing technical expertise with management responsibilities.',
          requiredSkills: ['team-management', 'firmware-tuning'],
          kpiBoosts: {
            uptime: 5,
            profitMargin: 3
          }
        },
        {
          path: 'Research',
          title: 'Mining Technology Innovator',
          description: 'Pioneer breakthrough technologies that give your operations a significant competitive edge.',
          requiredSkills: ['custom-firmware-development', 'cooling-optimization'],
          kpiBoosts: {
            efficiency: 7,
            energyCost: -0.005
          }
        }
      ]
    },
    {
      level: 'Leader',
      title: 'Chief Technology Officer',
      requiredExperience: 700,
      requiredYears: 10,
      pathOptions: [
        {
          path: 'Technical',
          title: 'Mining Technology Visionary',
          description: 'Become a recognized leader in mining technology, developing industry-defining innovations.',
          requiredSkills: ['custom-firmware-development', 'strategic-planning'],
          kpiBoosts: {
            efficiency: 10,
            profitMargin: 5
          }
        },
        {
          path: 'Management',
          title: 'Technology Operations Executive',
          description: 'Lead all technical aspects of mining operations with a focus on reliability and scale.',
          requiredSkills: ['team-management', 'strategic-planning'],
          kpiBoosts: {
            uptime: 7,
            efficiency: 5,
            profitMargin: 3
          }
        },
        {
          path: 'Entrepreneurial',
          title: 'Mining Tech Startup Founder',
          description: 'Launch your own mining technology company based on your groundbreaking innovations.',
          requiredSkills: ['custom-firmware-development', 'financial-planning'],
          kpiBoosts: {
            profitMargin: 10,
            efficiency: 5
          }
        }
      ]
    }
  ],
  specialChallenges: [
    challenges.find(c => c.id === 'hardware-upgrade')!,
    challenges.find(c => c.id === 'security-breach')!
  ],
  careerObjectives: [
    {
      title: 'Optimization Wizard',
      description: 'Increase firmware efficiency by 15%',
      target: {
        kpi: 'efficiency',
        value: 85
      },
      reward: 'Firmware Master Badge'
    },
    {
      title: 'Technical Innovator',
      description: 'Develop custom firmware solutions',
      target: {
        kpi: 'efficiency',
        value: 90
      },
      reward: 'Innovation Pioneer Badge'
    }
  ]
};
